import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { LoginPage } from './pages/Auth/LoginPage';
import { PaymentSuccededScreen } from './pages/Onboarding/PaymentSuccededScreen';

import { useEffect, useMemo, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { userRoleAdapter } from './adapters';
import { IUserRole } from './interfaces';
import { CreateAccountPage } from './pages/Auth/CreateAccountPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import ShareholderChangePasswordPage from './pages/Auth/ShareholderChangePasswordPage';
import { MailRoom } from './pages/DashboardManagement/MailRoom';
import { Onboarding } from './pages/Onboarding/Onboarding';
import { RoutesDynamicReturn, routes } from './routes/dynamicRoutes';
import { AuthGuard, CheckoutGuard, DashboardGuard, OnboardingCompletedGuard, OnboardingGuard } from './routes/guards';
import { IRoute } from './routes/interfaces/routes.interface';
import { AuthProvider, useAuth } from './store/auth';
import { PaymentFailedScreen } from './pages/Onboarding/PaymentFailedScreen';
import { AwaitingApproval } from './pages/AwaitingApproval';
import { ChoiceObjectives } from './pages/Onboarding/ChoiceObjective';
import { ChooseOnboardingGuard } from './routes/guards/ChooseOnboardingGuard';
import { CompanyTransferProvider } from './pages/Onboarding/CompanyTransfer/';
import { OnboardingProvider } from './pages/Onboarding/Context/OnboardingContext';

const NotFound = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      navigate(`/login?redirect=${location.pathname}`);
    }
  }, [isAuthenticated, isLoading, navigate, location.pathname]);

  return null;
};

const DashboardRoutes = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [userNavigation, setUserNavigation] = useState<RoutesDynamicReturn | null>();

  useEffect(() => {
    if (!user) {
      return;
    }

    const userRole: IUserRole = userRoleAdapter(user.role_id);
    setUserNavigation(routes[userRole]);
  }, [user]);

  const dynamicRoutes = useMemo(() => {
    if (!userNavigation) {
      return [];
    }

    return userNavigation?.routes.map((route: IRoute) => (
      <Route key={Math.random()} path={route.path} element={<AuthGuard element={route.component} />} />
    ));
  }, [userNavigation]);

  if (!isLoading && !isAuthenticated) {
    return null;
  }

  if (isLoading || !isAuthenticated || !user) {
    return <></>;
  }

  return (
    <Routes>
      <Route path="/dashboard/*">
        <Route
          path="*"
          element={
            <AuthGuard
              element={
                <OnboardingGuard element={<CheckoutGuard element={userNavigation?.DashboardContainerComponent} />} />
              }
            />
          }
        >
          <Route path="chat" element={<AuthGuard element={<MailRoom />} />} />

          {dynamicRoutes}
        </Route>
      </Route>
    </Routes>
  );
};

const Router = () => (
  <BrowserRouter>
    <DashboardRoutes />

    <Routes>
      <Route path="/" element={<DashboardGuard element={<LoginPage />} />} />
      <Route path="/payment-succeeded" element={<PaymentSuccededScreen />} />
      <Route path="/payment-failed" element={<PaymentFailedScreen />} />
      <Route path="/payment-awaiting-approval" element={<AuthGuard element={<AwaitingApproval />} />} />
      <Route path="/login" element={<DashboardGuard element={<LoginPage />} />} />
      <Route path="/forgot-password" element={<DashboardGuard element={<ForgotPasswordPage />} />} />
      <Route path="/reset-password" element={<DashboardGuard element={<ResetPasswordPage />} />} />
      <Route path="/shareholder-change-password/:idSlug" element={<DashboardGuard element={<ShareholderChangePasswordPage />} />} />
      <Route path="/register" element={<DashboardGuard element={<CreateAccountPage />} />} />
      <Route path="/company-transfer" element={<AuthGuard element={<CompanyTransferProvider />} />} />
      <Route
        path="/choose-onboarding"
        element={<AuthGuard element={<ChooseOnboardingGuard element={<ChoiceObjectives />} />} />}
      />
      <Route
        path="/onboarding"
        element={<AuthGuard element={<OnboardingCompletedGuard element={<Onboarding />} />} />}
      />
      <Route path="awaiting-approval" element={<AwaitingApproval />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  </BrowserRouter>
);

const App = () => (
  <>
    <ToastContainer />

    <AuthProvider>
      <OnboardingProvider>
        <Router />
      </OnboardingProvider>
    </AuthProvider>
  </>
);

export { App };
